import { Controller } from "@hotwired/stimulus";
import $ from "jquery";
import "summernote/dist/summernote-lite.min";
import DOMPurify from "dompurify";

// summernoteの言語ファイルがグローバルなjQueryを参照している
global.jQuery = $;
import("summernote/dist/lang/summernote-ja-JP").then(() => {
  $.extend(true, $.summernote.lang, {
    "ja-JP": {
      paragraph: {
        justify: "両端揃え",
      },
    },
  });
});

/**
 * Summernote
 * @example
 * <textarea data-controller="summernote"></textarea>
 * または
 * <div data-controller="summernote">
 *  <textarea data-summernote-target="textarea"></textarea>
 * </div>
 * オプションを渡す場合
 * <%= f.text_area :content, data: { controller: "summernote", summernote_options_value: { height: 300 } } %>
 */
export class SummernoteController extends Controller {
  static targets = ["textarea"];
  static values = {
    promoterId: Number,
    // オプション
    options: { type: Object, default: {} },
    // お問い合わせ用のツールバーを表示する
    isInquiry: { type: Boolean, default: false },
    // 入場方法用のツールバーを表示する
    isAdmission: { type: Boolean, default: false },
  };

  connect() {
    const el = this.#textarea;

    const fontSize = { fontSizes: ["8", "9", "10", "11", "12", "14", "16", "18", "24", "36"] };
    const defaultOptions = this.isAdmissionValue
      ? {
          // フォントサイズ、フォントカラー、太字、通常リスト
          toolbar: [
            ["font", ["fontsize", "color"]],
            ["style", ["bold", "clear"]],
            ["para", ["ul"]],
            ["view", ["fullscreen", "codeview"]],
          ],
        }
      : this.isInquiryValue
        ? {
            // フォントサイズ、フォントカラー、太字
            toolbar: [
              ["font", ["fontsize", "color"]],
              ["style", ["bold", "clear"]],
              ["view", ["fullscreen", "codeview"]],
            ],
          }
        : {
            // 通常設定
            toolbar: [
              ["fontsize", ["fontsize", "color"]],
              ["style", ["bold", "italic", "underline", "clear"]],
              ["para", ["ul", "ol", "paragraph"]],
              ["insert", ["link"]],
              ["view", ["fullscreen", "codeview"]],
            ],
          };
    // Summernoteの初期化
    $(el).summernote({
      lang: "ja-JP",
      ...fontSize,
      ...defaultOptions,
      ...this.optionsValue,
      callbacks: {
        // s3に画像をアップロード
        onImageUpload: (files) => {
          this.uploadImage(files[0]);
        },
        onChange: (contents) => {
          // 通常モードでの変更を反映
          sanitizeSummernoteHtml(el, contents, this.isInquiryValue, this.isAdmissionValue);
        },
        onBlurCodeview: () => {
          // Code Viewモードでの変更を反映
          const code = $(el).summernote("code");
          if (!code) {
            $(el).val("<p><br></p>");
          } else {
            sanitizeSummernoteHtml(el, code, this.isInquiryValue, this.isAdmissionValue);
          }
        },
        // 貼り付け時一部のstyleを除去
        onPaste: function (e) {
          const clipboard = (e.originalEvent || e).clipboardData;
          if (!clipboard) return;
          const html = clipboard.getData("text/html");
          if (!html) return;
          e.preventDefault();
          // overflow:hidden, heightを除去
          const cleaned = html.replace(/overflow\s*:\s*hidden;?/gi, "").replace(/height\s*:\s*\d+px;?/gi, "");
          $(this).summernote("pasteHTML", cleaned);
        },
      },
    });
  }

  disconnect() {
    const el = this.#textarea;
    $(el).summernote("destroy");
    $(el).remove();
  }

  async uploadImage(file) {
    const formData = new FormData();
    formData.append("filename", file);

    const promoterId = this.promoterIdValue;

    try {
      const response = await fetch(`/promoters/${promoterId}/editor_images`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (data.error) {
        alert(data.error);
        return;
      }
      const el = this.hasTextareaTarget ? this.textareaTarget : this.element;
      $(el).summernote("insertImage", data.url);
    } catch (error) {
      console.error("Image upload failed:", error);
    }
  }

  get #textarea() {
    return this.hasTextareaTarget ? this.textareaTarget : this.element;
  }
}

// XSS対策で不要なタグ・属性を除去
function sanitizeSummernoteHtml(el, html, isInquiry, isAdmission) {
  let sanitizedHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS:
      isInquiry || isAdmission
        ? isAdmission
          ? ["p", "span", "b", "font", "ul", "li", "br", "div"]
          : ["p", "span", "b", "font", "br", "div"]
        : ["p", "span", "b", "i", "u", "font", "ul", "ol", "li", "br", "a", "div"],
    ALLOWED_ATTR:
      isInquiry || isAdmission
        ? ["style", "color", "class"]
        : ["style", "color", "class", "href", "target", "src", "width", "height"],
  });

  if (html !== sanitizedHtml) {
    if (!sanitizedHtml) sanitizedHtml = "<p><br></p>";

    $(el).val(sanitizedHtml);
    $(el).summernote("code", sanitizedHtml);
  }
}
